import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type EyewitnessStatus =
  | 'new'
  | 'reviewed'
  | 'verified'
  | 'published'
  | 'rejected'
  | 'internal';

export interface EyewitnessSubmitter {
  id: string;
  name: string | null;
  email: string | null;
}

/** Listing-shape — slimmer than the detail payload. */
export interface EyewitnessListItem {
  id: string;
  locale: string;
  text: string;
  locationLabel: string | null;
  locationLat: string | null;
  locationLng: string | null;
  happenedAt: string;
  createdAt: string;
  status: EyewitnessStatus;
  aiSuggestedCategory: string | null;
  aiSummary: string | null;
  corroborationCount: number;
  corroboratedAt: string | null;
  submitter: EyewitnessSubmitter | null;
  publishedPostId: string | null;
}

export interface EyewitnessDetail extends EyewitnessListItem {
  moderatorNotes: string | null;
  aiDuplicateOfId: string | null;
  reviewedAt: string | null;
  reviewedBy: { id: string; name: string | null } | null;
}

export interface EyewitnessListResponse {
  items: EyewitnessListItem[];
}

export interface EyewitnessListParams {
  status?: EyewitnessStatus;
  locale?: string;
  limit?: number;
  beforeId?: string;
}

export function useEyewitnessReports(params: EyewitnessListParams) {
  return useQuery({
    queryKey: ['eyewitness', params],
    refetchInterval: 30_000,
    queryFn: () =>
      api.get<EyewitnessListResponse>('/admin/eyewitness', {
        ...(params.status ? { status: params.status } : {}),
        ...(params.locale ? { locale: params.locale } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.beforeId ? { beforeId: params.beforeId } : {}),
      }),
  });
}

export function useEyewitnessReport(id: string | null) {
  return useQuery({
    queryKey: ['eyewitness', id],
    enabled: !!id,
    queryFn: () => api.get<EyewitnessDetail>(`/admin/eyewitness/${id!}`),
  });
}

export interface ModerateInput {
  status: EyewitnessStatus;
  notes?: string | null;
  publishedPostId?: string | null;
  /** Convenience for "Publish" — server resolves this against the report's
   *  locale and fills `publishedPostId`. Either field works; if both, id wins. */
  publishedPostSlug?: string | null;
}

export interface EyewitnessMediaItem {
  id: string;
  kind: 'image' | 'video';
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  createdAt: string;
}

export function useEyewitnessMedia(reportId: string | null) {
  return useQuery({
    queryKey: ['eyewitness-media', reportId],
    enabled: !!reportId,
    queryFn: () =>
      api.get<{ items: EyewitnessMediaItem[] }>(`/admin/eyewitness/${reportId!}/media`),
  });
}

/** Path (admin-side) that streams a single media binary. */
export function eyewitnessMediaPath(reportId: string, mediaId: string): string {
  return `/admin/eyewitness/${reportId}/media/${mediaId}`;
}

export function useModerateEyewitness(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ModerateInput) =>
      api.patch<{
        id: string;
        status: EyewitnessStatus;
        reviewedAt: string | null;
        publishedPostId: string | null;
      }>(`/admin/eyewitness/${id!}`, input),
    onSuccess: () => {
      // Invalidate both the list (so chips re-count) and the detail.
      qc.invalidateQueries({ queryKey: ['eyewitness'] });
    },
  });
}
